/**
 * Configuración de los modos de creación de tickets y de sus guías: las
 * opciones que ve la persona al entrar a /nuevo-ticket, los pasos del modo
 * "paso a paso" y los pasos del tutorial del formulario avanzado.
 *
 * Es configuración en código (O/C): agregar un paso o un modo no requiere
 * tocar los componentes que la recorren.
 */

/**
 * Modos de creación. `key` es el valor del parámetro `?modo=` de la URL.
 * `showSidebar` muestra el panel lateral de ayuda (seguridad, tipos, tickets
 * recientes); el paso a paso lo oculta para no distraer.
 * @type {Array<{key: string, title: string, icon: string, color: string,
 *   description: string, bullets: string[], badge?: string,
 *   showSidebar: boolean}>}
 */
export const CREATE_MODES = [
  {
    key: 'guiado',
    title: 'Paso a paso',
    icon: 'fa-shoe-prints',
    color: '#14b8a6',
    description:
      'Le hacemos una pregunta a la vez para completar el ticket sin olvidar nada.',
    bullets: [
      'Una pregunta por pantalla',
      'Ideal si es su primera vez',
      'Revisión final antes de enviar',
    ],
    badge: 'Recomendado',
    showSidebar: false,
  },
  {
    key: 'formulario',
    title: 'Formulario avanzado',
    icon: 'fa-sliders',
    color: '#3b82f6',
    description:
      'Todos los campos en una sola vista, para quien ya conoce el proceso.',
    bullets: [
      'Control total de la clasificación',
      'Más rápido si ya sabe qué poner',
      'Incluye un tutorial guiado',
    ],
    showSidebar: true,
  },
]

/** Busca un modo por su clave (o `undefined`). */
export const getCreateMode = (key) => CREATE_MODES.find((m) => m.key === key)

/**
 * Preguntas guiadas del paso "Detalle" del modo paso a paso. Las respuestas se
 * unen en la descripción con `composeDescription`.
 */
export const DETAIL_QUESTIONS = [
  {
    key: 'what',
    label: '¿Qué sucede?',
    placeholder: 'Ej: Al abrir el reporte de ventas sale un error en blanco.',
    required: true,
  },
  {
    key: 'since',
    label: '¿Desde cuándo o cada cuánto pasa?',
    placeholder: 'Ej: Desde hoy en la mañana, cada vez que lo intento.',
  },
  {
    key: 'expected',
    label: '¿Qué esperaba que ocurriera?',
    placeholder: 'Ej: Que se descargara el reporte en Excel.',
  },
  {
    key: 'tried',
    label: '¿Qué ha intentado?',
    placeholder: 'Ej: Reinicié el navegador y probé con otro usuario.',
  },
]

/**
 * Pasos del modo paso a paso. `fields` son los campos del borrador que se
 * validan antes de avanzar.
 * @type {Array<{key: string, title: string, subtitle: string, icon: string, fields: string[]}>}
 */
export const WIZARD_STEPS = [
  {
    key: 'type',
    title: '¿Qué necesita?',
    subtitle: 'Elija la opción que mejor describe su caso.',
    icon: 'fa-shapes',
    fields: ['type'],
  },
  {
    key: 'origin',
    title: '¿Dónde ocurre?',
    subtitle: 'Seleccione el sistema o equipo afectado.',
    icon: 'fa-server',
    fields: ['systemOrigin'],
  },
  {
    key: 'title',
    title: 'Póngale un título',
    subtitle: 'Una frase corta que resuma el caso.',
    icon: 'fa-heading',
    fields: ['title'],
  },
  {
    key: 'detail',
    title: 'Cuéntenos más',
    subtitle: 'Responda lo que sepa; sólo la primera pregunta es obligatoria.',
    icon: 'fa-align-left',
    fields: ['description'],
  },
  {
    key: 'priority',
    title: '¿Qué tan grave es?',
    subtitle: 'Con esto calculamos la prioridad y el tiempo de atención.',
    icon: 'fa-gauge-high',
    fields: [],
  },
  {
    key: 'files',
    title: 'Adjuntos',
    subtitle: 'Opcional: capturas de pantalla o archivos que ayuden.',
    icon: 'fa-paperclip',
    fields: [],
  },
  {
    key: 'review',
    title: 'Revise y envíe',
    subtitle: 'Confirme que todo esté correcto.',
    icon: 'fa-clipboard-check',
    fields: ['title', 'systemOrigin', 'type', 'description'],
  },
]

/**
 * Pasos del tutorial del formulario avanzado. `target` es el valor del
 * atributo `data-tour` del elemento que se resalta.
 * @type {Array<{target: string, title: string, body: string}>}
 */
export const ADVANCED_TOUR_STEPS = [
  {
    target: 'title',
    title: 'Título',
    body: 'Resuma el caso en una línea. Ej: «No carga el reporte de ventas en KPIS».',
  },
  {
    target: 'origin',
    title: 'Plataforma',
    body: 'Indique el sistema o equipo afectado; así llega al equipo correcto.',
  },
  {
    target: 'type',
    title: 'Tipo de registro',
    body: 'Primero la familia (Soporte o Desarrollo) y luego el tipo. Si algo dejó de funcionar, es un Incidente.',
  },
  {
    target: 'priority',
    title: 'Impacto y urgencia',
    body: 'Impacto: a cuántos afecta. Urgencia: qué tan pronto se necesita. La prioridad se calcula sola.',
  },
  {
    target: 'description',
    title: 'Descripción',
    body: 'Explique qué pasó, cómo reproducirlo y qué esperaba. Incluya usuario afectado y hora aproximada.',
  },
  {
    target: 'files',
    title: 'Adjuntos',
    body: 'Arrastre capturas o archivos. Una imagen del error ahorra mucho tiempo.',
  },
  {
    target: 'submit',
    title: 'Enviar',
    body: 'Al crear el ticket se abre su detalle, donde puede seguir su avance y comentar.',
  },
]
